import { useState } from 'react'
import { CATEGORIAS_ACADEMIA, ESTADOS_ACADEMIA, type Rec } from '../../shared/schema'
import { quickUpdate, useRecords } from '../lib/api'
import { useEditor } from '../lib/useOpen'
import { Link } from '../lib/router'
import { fmtDay, today } from '../lib/format'
import { Kanban } from '../components/Boards'
import { RecordForm } from '../components/RecordForm'
import { Avatar, Badge, Button, Empty, ErrorNote, Icon, PageHeader, Segmented, Skeleton, Star, toneOf, useToast } from '../components/ui'

const WEIGHT: Record<string, number> = { 'Idea': 0, 'En preparación': 0.25, 'Grabado': 0.5, 'Editado': 0.75, 'Listo para publicar': 1 }

/** Avance: promedio ponderado del estado de clases y recursos */
export function academiaProgress(data: Rec[]) {
  const piezas = data.filter(d => d.nivel === 'Clase' || d.nivel === 'Recurso')
  const pct = piezas.length ? piezas.reduce((s, d) => s + (WEIGHT[d.estado] ?? 0), 0) / piezas.length : 0
  return { pct, total: piezas.length, listos: piezas.filter(d => d.estado === 'Listo para publicar').length }
}

type View = 'panel' | 'cursos' | 'biblioteca' | 'recursos' | 'plan'

export function Academia() {
  const { data, loading, error, refresh } = useRecords('academia')
  const tareas = useRecords('tareas').data
  const ed = useEditor(data)
  const toast = useToast()
  const [view, setView] = useState<View>('panel')
  const [cat, setCat] = useState('')

  const cursos = data.filter(d => d.nivel === 'Curso')
  const children = (id: string) => data.filter(d => d.perteneceA === id)
  const prog = academiaProgress(data)
  const tareasAca = tareas.filter(t => t.area === 'Academia' || t.academia)

  return (
    <div className="page academia">
      <PageHeader eyebrow="Módulo 7 · uso interno" title="Academia REVELA" subtitle="Formación práctica para asesores independientes y pequeñas inmobiliarias. Por ahora, el taller donde la construimos."
        actions={<Button variant="primary" icon="plus" onClick={() => ed.create({ nivel: 'Clase', estado: 'Idea' })}>Nueva clase o recurso</Button>} />

      <Segmented value={view} onChange={setView} options={[
        { value: 'panel', label: 'Panel', icon: 'home' },
        { value: 'cursos', label: 'Cursos y módulos', icon: 'layers' },
        { value: 'biblioteca', label: 'Biblioteca', icon: 'book' },
        { value: 'recursos', label: 'Recursos', icon: 'file' },
        { value: 'plan', label: 'Plan de desarrollo', icon: 'kanban' },
      ]} />

      {error && <ErrorNote error={error} onRetry={refresh} />}
      {loading ? <Skeleton rows={4} /> : (
        <>
          {view === 'panel' && (
            <div className="aca-panel">
              <section className="aca-hero panel">
                <div className="aca-ring">
                  <svg viewBox="0 0 120 120" width="132" height="132" role="img" aria-label={`Avance ${Math.round(prog.pct * 100)} %`}>
                    <circle cx="60" cy="60" r="50" fill="none" stroke="var(--line)" strokeWidth="9" />
                    <circle cx="60" cy="60" r="50" fill="none" stroke="var(--terracota)" strokeWidth="9" strokeLinecap="round" strokeDasharray={`${Math.PI * 100 * prog.pct} ${Math.PI * 100}`} transform="rotate(-90 60 60)" />
                    <text x="60" y="64" textAnchor="middle" className="ring-num">{Math.round(prog.pct * 100)}%</text>
                  </svg>
                </div>
                <div>
                  <span className="ctx-label">Estado general</span>
                  <h2 className="aca-title">{prog.pct < 0.25 ? 'Sembrando ideas' : prog.pct < 0.6 ? 'En plena producción' : prog.pct < 0.9 ? 'Puliendo el material' : 'Lista para lanzar'}</h2>
                  <p className="muted">{prog.listos} de {prog.total} clases y recursos listos para publicar. El avance pondera cada estado: idea 0 %, en preparación 25 %, grabado 50 %, editado 75 %, listo 100 %.</p>
                  <div className="aca-states">{ESTADOS_ACADEMIA.map(e => <span key={e}><span className={`dot tone-${toneOf(e)}`} />{e} <strong>{data.filter(d => d.estado === e && d.nivel !== 'Curso' && d.nivel !== 'Módulo').length}</strong></span>)}</div>
                </div>
              </section>
              <div className="aca-cols">
                <AcaList title="Cursos y módulos en preparación" items={data.filter(d => (d.nivel === 'Curso' || d.nivel === 'Módulo') && d.estado !== 'Listo para publicar')} onOpen={ed.open} />
                <AcaList title="Ideas para futuras clases" items={data.filter(d => d.nivel === 'Clase' && d.estado === 'Idea')} onOpen={ed.open} />
                <AcaList title="Clases grabadas" items={data.filter(d => d.nivel === 'Clase' && ['Grabado', 'Editado', 'Listo para publicar'].includes(d.estado))} onOpen={ed.open} />
                <AcaList title="Recursos descargables" items={data.filter(d => d.nivel === 'Recurso')} onOpen={ed.open} />
                <section className="panel">
                  <header className="panel-head"><h2>Próximas tareas</h2><Link to="/tareas" className="panel-link">Tareas</Link></header>
                  <ul className="task-list">
                    {tareasAca.filter(t => t.estado !== 'Hecha').sort((a, b) => (a.vence || '9').localeCompare(b.vence || '9')).slice(0, 6).map(t => (
                      <li key={t.id} className={t.vence && t.vence < today() ? 'overdue' : ''}>
                        <button type="button" className="check" aria-label="Marcar hecha" onClick={async () => { try { await quickUpdate('tareas', t, { estado: 'Hecha' }); toast('Tarea completada') } catch (e: any) { toast(e.message, 'error') } }}><Icon name="check" size={13} /></button>
                        <Link to={`/tareas?abrir=${t.id}`} className="task-title">{t.titulo}</Link><span className="task-meta">{fmtDay(t.vence)}</span><Avatar name={t.socia} />
                      </li>
                    ))}
                    {!tareasAca.some(t => t.estado !== 'Hecha') && <li className="muted">Sin tareas pendientes.</li>}
                  </ul>
                  <Link to="/tareas?nuevo=1&area=Academia" className="inline-link pad-x"><Icon name="plus" size={14} /> Nueva tarea de la Academia</Link>
                </section>
              </div>
            </div>
          )}

          {view === 'cursos' && (
            <div className="courses">
              {cursos.map(c => (
                <section key={c.id} className="course panel">
                  <header className="course-head" onClick={() => ed.open(c)}>
                    <div><span className="ctx-label">{c.categoria}</span><h2>{c.titulo}</h2>{c.descripcion && <p className="muted">{c.descripcion}</p>}</div>
                    <div className="row gap-s"><Badge>{c.estado}</Badge><Avatar name={c.socia} /></div>
                  </header>
                  <ol className="modules">
                    {children(c.id).filter(m => m.nivel === 'Módulo').map(m => (
                      <li key={m.id} className="module">
                        <button type="button" className="module-head" onClick={() => ed.open(m)}><Icon name="layers" size={15} /><strong>{m.titulo}</strong><Badge>{m.estado}</Badge></button>
                        <ul className="lessons">
                          {children(m.id).map(l => (
                            <li key={l.id}>
                              <button type="button" onClick={() => ed.open(l)}>
                                <span className={`dot tone-${toneOf(l.estado)}`} /><span className="grow">{l.titulo}</span>
                                {children(l.id).length > 0 && <span className="muted small">{children(l.id).length} recurso{children(l.id).length > 1 ? 's' : ''}</span>}
                                <span className="muted small">{l.estado}</span>
                              </button>
                            </li>
                          ))}
                          <li><button type="button" className="add-row" onClick={() => ed.create({ nivel: 'Clase', perteneceA: m.id, categoria: c.categoria, estado: 'Idea' })}><Icon name="plus" size={14} /> Agregar clase</button></li>
                        </ul>
                      </li>
                    ))}
                  </ol>
                  <button type="button" className="add-row" onClick={() => ed.create({ nivel: 'Módulo', perteneceA: c.id, categoria: c.categoria, estado: 'Idea' })}><Icon name="plus" size={14} /> Agregar módulo</button>
                </section>
              ))}
              <button type="button" className="new-course" onClick={() => ed.create({ nivel: 'Curso', estado: 'Idea' })}><Star size={18} /><span>Crear un curso nuevo</span></button>
            </div>
          )}

          {view === 'biblioteca' && (
            <div className="library">
              <div className="chips">
                <button type="button" className={`chip ${!cat ? 'on' : ''}`} onClick={() => setCat('')}>Todas</button>
                {CATEGORIAS_ACADEMIA.map(c => <button type="button" key={c} className={`chip ${cat === c ? 'on' : ''}`} onClick={() => setCat(cat === c ? '' : c)}>{c}</button>)}
              </div>
              <div className="cat-grid">
                {CATEGORIAS_ACADEMIA.filter(c => !cat || c === cat).map((c, i) => {
                  const items = data.filter(d => d.categoria === c && d.nivel !== 'Curso' && d.nivel !== 'Módulo')
                  return (
                    <section key={c} className="cat-card">
                      <header><span className="cat-n">{String(i + 1).padStart(2, '0')}</span><h3>{c}</h3><span className="count">{items.length}</span></header>
                      <ul>{items.map(d => <li key={d.id}><button type="button" onClick={() => ed.open(d)}><span className={`dot tone-${toneOf(d.estado)}`} /><span className="grow">{d.titulo}</span><span className="muted small">{d.nivel}</span></button></li>)}</ul>
                      <button type="button" className="add-row" onClick={() => ed.create({ nivel: 'Clase', categoria: c, estado: 'Idea' })}><Icon name="plus" size={14} /> Agregar</button>
                    </section>
                  )
                })}
              </div>
            </div>
          )}

          {view === 'recursos' && (
            <div className="res-grid">
              {data.filter(d => d.nivel === 'Recurso').map(d => (
                <article key={d.id} className="res-card" onClick={() => ed.open(d)} tabIndex={0}>
                  <div className="res-icon"><Icon name={d.tipoRecurso === 'Checklist' ? 'checksq' : d.tipoRecurso === 'Recurso de IA' ? 'layers' : 'file'} size={22} /></div>
                  <span className="ctx-label">{d.tipoRecurso || 'Recurso'}</span>
                  <strong>{d.titulo}</strong>
                  <span className="muted small">{d.categoria}</span>
                  <div className="row between"><Badge>{d.estado}</Badge><span className="muted small">{(d.material?.length ?? 0) + (d.enlaces?.length ?? 0)} archivos</span></div>
                </article>
              ))}
              <button type="button" className="res-card res-add" onClick={() => ed.create({ nivel: 'Recurso', estado: 'Idea' })}><Icon name="plus" size={22} /><span>Nuevo recurso</span></button>
              {!data.some(d => d.nivel === 'Recurso') && <Empty title="Sin recursos todavía" text="Plantillas, checklists, guías, prompts de IA y herramientas recomendadas." />}
            </div>
          )}

          {view === 'plan' && (
            <Kanban columns={ESTADOS_ACADEMIA.map(e => ({ key: e, tone: toneOf(e) }))} items={data.filter(d => d.nivel !== 'Curso')} groupBy={d => d.estado}
              onCardClick={ed.open}
              onMove={async (d, e) => { try { await quickUpdate('academia', d, { estado: e }); toast(`“${d.titulo}” → ${e}`) } catch (err: any) { toast(err.message, 'error') } }}
              render={d => (
                <>
                  <span className="ctx-label">{d.nivel}{d.tipoRecurso ? ` · ${d.tipoRecurso}` : ''}</span>
                  <strong>{d.titulo}</strong>
                  <div className="kc-foot"><span>{d.fecha ? fmtDay(d.fecha) : ''}</span><Avatar name={d.socia} /></div>
                </>
              )} />
          )}
        </>
      )}

      <RecordForm col="academia" open={ed.isOpen} record={ed.editing} defaults={ed.creating ?? undefined} onClose={ed.close}
        top={() => ed.editing && <AcaContext d={ed.editing} all={data} tareas={tareas} onOpen={ed.open} />} />
    </div>
  )
}

function AcaList({ title, items, onOpen }: { title: string; items: Rec[]; onOpen: (r: Rec) => void }) {
  return (
    <section className="panel">
      <header className="panel-head"><h2>{title}</h2><span className="count">{items.length}</span></header>
      <ul className="aca-list">
        {items.slice(0, 6).map(d => <li key={d.id}><button type="button" onClick={() => onOpen(d)}><span className={`dot tone-${toneOf(d.estado)}`} /><span className="grow">{d.titulo}</span><span className="muted small">{d.nivel}</span></button></li>)}
        {!items.length && <li className="muted">Nada por ahora.</li>}
      </ul>
    </section>
  )
}

function AcaContext({ d, all, tareas, onOpen }: { d: Rec; all: Rec[]; tareas: Rec[]; onOpen: (r: Rec) => void }) {
  const parent = all.find(x => x.id === d.perteneceA)
  const kids = all.filter(x => x.perteneceA === d.id)
  const ts = tareas.filter(t => t.academia === d.id)
  if (!parent && !kids.length && !ts.length) return null
  return (
    <div className="context">
      {parent && <div className="ctx-block"><span className="ctx-label">Pertenece a</span><button type="button" className="chip" onClick={() => onOpen(parent)}>{parent.nivel}: {parent.titulo}</button></div>}
      {kids.length > 0 && <div className="ctx-block"><span className="ctx-label">Contiene</span><div className="chips">{kids.map(k => <button type="button" key={k.id} className="chip" onClick={() => onOpen(k)}>{k.titulo}</button>)}</div></div>}
      {ts.length > 0 && <div className="ctx-block"><span className="ctx-label">Tareas</span><ul className="ctx-list">{ts.map(t => <li key={t.id}><Badge>{t.estado}</Badge> {t.titulo}</li>)}</ul></div>}
    </div>
  )
}
